import { Theme } from "@design-system/themes"
import { createStyles, styleConfigs, Styles } from "../styles"
import { allStyleProps, svgStyleProps, imageStyleProps } from "./style-props"
import { UseStyleResult, UseStylePropsProps, StylePropConfigs } from "../types"

export function useStyleProps<P>(
  props: P,
  type?: React.ElementType,
): UseStyleResult<P> {
  const styledProps = props as UseStylePropsProps<P>
  const { style: providedStyle } = styledProps
  let styleProps: StylePropConfigs
  if (type === "img") {
    styleProps = imageStyleProps
  } else if (type === "svg") {
    styleProps = svgStyleProps
  } else {
    styleProps = allStyleProps
  }

  // props without styled props
  const newProps = Object.keys(styledProps).reduce((acc, name) => {
    if (!styleProps[name]) {
      acc[name] = styledProps[name]
    }
    return acc
  }, {})

  const styles = createStyles((theme: Theme) => {
    const styleObject = {}

    // e.g. <View m="small" padding="large" foo="bar" width={100} height={undefined} />
    Object.keys(styledProps).forEach((name) => {
      const value = styledProps[name]

      // e.g. name is `foo` or `height`, return
      if (!styleProps[name] || value === undefined) {
        return
      }

      const styleProp = styleProps[name]

      // e.g. name="m", styleProp = { alias: ['margin'] }
      if (typeof styleProp !== "boolean") {
        const alias = styleProp.alias
        // e.g. aname = "margin"
        alias.forEach((aname) => {
          styleObject[aname] = getStyle(theme, aname, value)
        })
      } else {
        // e.g. name = "padding", value = "large"
        styleObject[name] = getStyle(theme, name, value)
      }
    })

    return {
      propsStyle: Object.keys(styleObject).length ? styleObject : undefined,
    } as {
      propsStyle?: object
    }
  })

  const style = Styles.compose(providedStyle, styles.propsStyle)

  return {
    ...newProps,
    style,
  } as UseStyleResult<P>
}

// get style by name and value
// e.g.
// name="margin", value="large" => { margin: theme.spaces.large }
// name="width", value="100" => { width: 100 }
function getStyle(theme: Theme, name: string, value: string) {
  const tokenKeys = styleConfigs[name]?.tokens
  // no tokens config, e.g. name="display", value="none"
  // value is not string (token name can be only string), e.g. name="width", value={100}
  if (!Array.isArray(tokenKeys) || typeof value !== "string") {
    return value
  }

  for (let i = 0; i < tokenKeys.length; i++) {
    const tokenKey = tokenKeys[i] // e.g. colors, spaces, sizes, fontSizes
    const themeProp = theme[tokenKey] // e.g. theme.colors, theme.spaces, theme.colors
    // not a valid theme key
    if (!themeProp) {
      continue
    }

    // might be negative, e.g. margin="-spacing32"
    let possibleNegative = false
    let propValue = value
    if (value.startsWith("-")) {
      possibleNegative = true
      propValue = value.substr(1)
    }
    let val = themeProp[propValue]
    if (val !== undefined) {
      if (possibleNegative) {
        if (typeof val === "number") {
          val = -val
        } else if (typeof val === "string") {
          val = `-${val}`
        }
      }
      return val
    }
  }

  // return undefined if the value can be tokens only
  // return the original value if the value is not limited to tokens
  return styleConfigs[name]?.tokensOnly ? undefined : value
}
